/**
 * Shared helpers + derivation logic for the property detail page.
 *
 * `DealInputsState` is the central tree the panels read + write to. The
 * detail page owns it; child panels stay stateless. On change, the page
 * debounces + persists to `property_setting` via `useDealSettings`.
 *
 * `deriveMetrics()` runs the pure calculators from `@/lib/calculators` over
 * the current state and returns the universal derived numbers most panels
 * need (NOI, cashflow, hard-money payment, MAO, etc.). Strategy-specific
 * panels (BRRRR refi, Flip ROI, etc.) layer additional derivations on top.
 */
import {
  amortizedEmi,
  capRate,
  cashOnCash,
  closingCost as closingCostFn,
  computeMonthlyCashflow,
  downPayment as downPaymentFn,
  dscr,
  grm,
  interestOnlyMonthly,
  loanAmount as loanAmountFn,
  mao as maoFn,
  onePctRule,
  originationFee,
  parseMoney,
  round2,
  termInMonths,
} from "@/lib/calculators";
import { DEFAULT_DEAL_SETTINGS } from "@/hooks/use-deal-settings";
import type { MockProperty } from "@/data/mockProperties";
import type {
  BrrrrSettings,
  CashflowOverrides,
  FlipSettings,
  LoanInputs,
  MaoSettings,
  RehabLineItemRow,
  RehabLineItemsDiff,
  RehabSettings,
  RentalSettings,
  Strategy,
} from "@/types/pipeline";

/** Source listing data the detail page receives (snapshot from search/portfolio). */
export interface PropertySnapshot {
  /** Stable id used as `pipeline_properties.property_id`. */
  id: string;
  imgUrl?: string | null;
  /** Displayable price string (e.g. "$165,000") or numeric. */
  price?: string | number | null;
  addr1?: string | null;
  addr2?: string | null;
  bd?: number | null;
  ba?: number | null;
  sqft?: string | number | null;
  /** Estimated rent string (e.g. "$1,340/mo") or numeric. */
  rent?: string | number | null;
  /** Days on market (display only). */
  days?: string | null;
  /** Exact Section 8 SAFMR for this property's ZIP + bedroom count (from the API). */
  section8Fmr?: number | null;
}

/**
 * Narrow a listing / `pipeline_properties.property_data` blob down to the
 * snapshot the detail page + `buildInitialInputs` consume. Both key spellings
 * are accepted because the blob is written by several producers: Find search
 * results use the `MockProperty` names (`address`, `bedrooms`, `estimatedRent`),
 * older pipeline rows use the mockup's short names (`addr1`, `bd`, `rent`).
 */
export function toPropertySnapshot(p: MockProperty): PropertySnapshot {
  const r = p as unknown as Record<string, unknown>;
  return {
    id: String(p.id ?? ""),
    imgUrl: (r.imageUrl as string | undefined) ?? (r.img as string | undefined) ?? null,
    price: (r.price as string | number | undefined) ?? null,
    addr1: (r.address as string | undefined) ?? (r.addr1 as string | undefined) ?? null,
    addr2: (r.city as string | undefined)
      ? `${r.city}${r.state ? ", " + (r.state as string) : ""}${r.zip ? " " + (r.zip as string) : ""}`
      : ((r.addr2 as string | undefined) ?? null),
    bd: (r.bedrooms as number | undefined) ?? (r.bd as number | undefined) ?? null,
    ba: (r.bathrooms as number | undefined) ?? (r.ba as number | undefined) ?? null,
    sqft: (r.squareFeet as string | number | undefined) ?? (r.sqft as string | number | undefined) ?? null,
    rent: (r.estimatedRent as string | number | undefined) ?? (r.rent as string | number | undefined) ?? null,
    days: (r.daysOnMarket as string | undefined) ?? (r.days as string | undefined) ?? null,
    section8Fmr: (r.section8Fmr as number | undefined) ?? null,
  };
}

/** All editable state on the detail page, normalized for the calculators. */
export interface DealInputsState {
  /** Deal Inputs row */
  purchase: number;
  rehab: number;
  arv: number;
  rent: number;

  /** Closing cost */
  closingPct: number;
  closingAuto: boolean;

  /** MAO */
  mao: MaoSettings;

  /** Section 8 toggle */
  useSection8Rent: boolean;

  /** Financing master toggle — when off, all loan figures zero out (all-cash deal). */
  financingEnabled: boolean;

  /**
   * BRRRR Step 1 (hard money) vs Step 2 (refi) selector — a transient UI view,
   * NOT persisted (the owned-property active slot lives in the loans table).
   */
  loanView: "short" | "long";

  /** Strategy-grouped settings + financing knobs */
  flip: FlipSettings;
  brrrr: BrrrrSettings;
  rental: RentalSettings;

  /** Rehab line items */
  rehabLineItems: RehabSettings["line_items"];
  rehabCustomItems: RehabSettings["custom_items"];

  /** Monthly cash flow overrides */
  cashflowOverrides: CashflowOverrides;
}

/** Default rehab template — 6 buckets, each defaulting to $0 for the user to fill in. */
export function buildDefaultRehabLineItems(): NonNullable<
  RehabSettings["line_items"]
> {
  return [
    { label: "Interior paint & flooring", amount: 0 },
    { label: "Kitchen refresh", amount: 0 },
    { label: "Bath updates", amount: 0 },
    { label: "HVAC / electrical / plumbing", amount: 0 },
    { label: "Exterior & landscaping", amount: 0 },
    { label: "Contingency", amount: 0 },
  ];
}

/**
 * Apply a new MAO percent. While MAO is locked to the purchase price, the new
 * % flows through to Purchase Price live; `prev_purchase` is preserved so
 * toggling MAO off still restores the original purchase.
 */
export function applyMaoPct(
  state: DealInputsState,
  pct: number,
): Partial<DealInputsState> {
  const patch: Partial<DealInputsState> = { mao: { ...state.mao, pct } };
  if (state.mao.use_as_purchase) {
    patch.purchase = Math.round(maoFn(state.arv, state.rehab, pct));
  }
  return patch;
}

/**
 * Toggle "Use MAO as Purchase Price". Switching on snapshots the current
 * purchase into `mao.prev_purchase` and locks Purchase Price to the MAO value;
 * switching off restores that snapshot.
 */
export function toggleMaoAsPurchase(
  state: DealInputsState,
  on: boolean,
): Partial<DealInputsState> {
  if (on) {
    return {
      mao: {
        ...state.mao,
        use_as_purchase: true,
        prev_purchase: state.purchase,
      },
      purchase: Math.round(maoFn(state.arv, state.rehab, state.mao.pct)),
    };
  }
  return {
    mao: { ...state.mao, use_as_purchase: false, prev_purchase: null },
    purchase: state.mao.prev_purchase ?? state.purchase,
  };
}

const DEFAULT_LOAN_SHORT: LoanInputs = { ltv: 85, rate: 11, term: 12, points: 2 };
const DEFAULT_LOAN_LONG: LoanInputs = { ltv: 80, rate: 7, term: 30, points: 1 };

/**
 * Stamp the financing toggle onto each loan slot present in a strategy block.
 * The find/detail page has a single `financingEnabled` flag, but it is
 * persisted per-slot inside the property_setting blobs (`*_hard_money`,
 * `*_long_term`) as `is_enabled` — the pre-acquisition memory of the toggle.
 */
function withEnabledFlag<
  T extends { hard_money?: LoanInputs; long_term?: LoanInputs },
>(block: T, enabled: boolean): T {
  return {
    ...block,
    ...(block.hard_money
      ? { hard_money: { ...block.hard_money, is_enabled: enabled } }
      : {}),
    ...(block.long_term
      ? { long_term: { ...block.long_term, is_enabled: enabled } }
      : {}),
  };
}

/** The loan slots that carry the financing toggle for a given strategy. */
function financingSlots(
  p: PersistedSettings,
  strategy: Strategy,
): Array<LoanInputs | undefined> {
  if (strategy === "Flip") return [p.flip?.hard_money];
  if (strategy === "Rental") return [p.rental?.long_term];
  return [p.brrrr?.hard_money, p.brrrr?.long_term];
}

/**
 * Read the financing toggle back from the persisted blobs. Financing is tracked
 * per-strategy (each strategy's loan slots carry their own `is_enabled`), so we
 * read the slots for the active strategy. When no strategy is given, fall back
 * to "first slot with a flag". Legacy rows (no flag) default to enabled.
 */
function readFinancingEnabled(p: PersistedSettings, strategy?: Strategy): boolean {
  const slots = strategy
    ? financingSlots(p, strategy)
    : [
        p.flip?.hard_money,
        p.brrrr?.hard_money,
        p.brrrr?.long_term,
        p.rental?.long_term,
      ];
  for (const slot of slots) {
    if (slot && typeof slot.is_enabled === "boolean") return slot.is_enabled;
  }
  return true;
}

/**
 * Initial deal inputs derived from the property snapshot. Used both for
 * a brand-new deal AND merged on top of persisted settings so any missing
 * fields fall back to sensible defaults.
 */
export function buildInitialInputs(snapshot: PropertySnapshot): DealInputsState {
  const price = parseMoney(snapshot.price ?? 0);
  const rentMonthly = parseMoney(snapshot.rent ?? 0);
  const rehab = 0;
  const arv = Math.round(price * 1.28);
  return {
    purchase: price,
    rehab,
    arv,
    rent: rentMonthly,
    closingPct: DEFAULT_DEAL_SETTINGS.closing_pct,
    closingAuto: DEFAULT_DEAL_SETTINGS.closing_auto,
    mao: { ...DEFAULT_DEAL_SETTINGS.mao },
    useSection8Rent: false,
    financingEnabled: true,
    loanView: "short",
    flip: {
      hold_months: 6,
      // Percent only, deliberately: `sell_value` wins over `sell_pct` wherever
      // the selling cost is consumed, so seeding it here would pin the dollars
      // to this guessed ARV (price × 1.28) and stop them tracking the real one
      // the user enters. It's written the moment either box is edited.
      sell_pct: 7,
      taxes_monthly: Math.round(price * 0.0018),
      utilities_monthly: 100,
      hard_money: { ...DEFAULT_LOAN_SHORT },
    },
    brrrr: {
      hard_money: { ...DEFAULT_LOAN_SHORT },
      long_term: { ...DEFAULT_LOAN_LONG, ltv: 75 }, // long_term.ltv = refi LTV against ARV
      hold_months: 6,
      taxes_monthly: Math.round(price * 0.0018),
      utilities_monthly: 100,
      mcf_enabled: false,
      mcf_delay: 0,
    },
    rental: {
      long_term: { ...DEFAULT_LOAN_LONG },
    },
    rehabLineItems: buildDefaultRehabLineItems(),
    rehabCustomItems: [],
    cashflowOverrides: {},
  };
}

/** Persisted property_setting row shape consumed by mergeWithPersisted. */
export interface PersistedSettings {
  purchase_price: number | null;
  repair_cost: number | null;
  after_repair_value: number | null;
  monthly_rent: number | null;
  flip: FlipSettings;
  brrrr: BrrrrSettings;
  rental: RentalSettings;
  monthly_cash_flow: CashflowOverrides;
  closing_pct: number;
  closing_auto: boolean;
  mao: MaoSettings;
  use_section8_rent: boolean;
}

/**
 * Merge initial defaults with persisted property_setting fields. Rehab
 * scope items come from a separate rehab_line_items query and are merged in
 * via `rehabRows` so each UI item carries its DB `id`.
 *
 * `strategy` (when provided) scopes the financing-toggle read to that
 * strategy's loan slots — financing is tracked per-strategy.
 */
export function mergeWithPersisted(
  initial: DealInputsState,
  persisted: PersistedSettings | null,
  rehabRows: RehabLineItemRow[] | null = null,
  strategy?: Strategy,
): DealInputsState {
  const lineItems = rehabRows
    ? rehabRows
        .filter((r) => r.category === "standard")
        .map((r) => ({
          id: r.id,
          label: r.label ?? "",
          amount: Number(r.estimated_cost) || 0,
        }))
    : initial.rehabLineItems;
  const customItems = rehabRows
    ? rehabRows
        .filter((r) => r.category === "custom")
        .map((r) => ({
          id: r.id,
          label: r.label ?? "",
          amount: Number(r.estimated_cost) || 0,
        }))
    : initial.rehabCustomItems;

  if (!persisted) {
    return {
      ...initial,
      // Rows may exist for a deal that doesn't yet have a property_setting
      // row (e.g. legacy data). Keep them visible either way.
      rehabLineItems: lineItems,
      rehabCustomItems: customItems,
    };
  }
  return {
    ...initial,
    purchase: persisted.purchase_price ?? initial.purchase,
    rehab: persisted.repair_cost ?? initial.rehab,
    arv: persisted.after_repair_value ?? initial.arv,
    rent: persisted.monthly_rent ?? initial.rent,
    closingPct: persisted.closing_pct ?? initial.closingPct,
    closingAuto: persisted.closing_auto ?? initial.closingAuto,
    mao: { ...initial.mao, ...persisted.mao },
    useSection8Rent: persisted.use_section8_rent ?? initial.useSection8Rent,
    // Restore the financing toggle from the active strategy's blob is_enabled.
    financingEnabled: readFinancingEnabled(persisted, strategy),
    flip: {
      ...initial.flip,
      ...persisted.flip,
      hard_money: { ...initial.flip.hard_money, ...(persisted.flip.hard_money ?? {}) },
    },
    brrrr: {
      ...initial.brrrr,
      ...persisted.brrrr,
      hard_money: { ...initial.brrrr.hard_money, ...(persisted.brrrr.hard_money ?? {}) },
      long_term: { ...initial.brrrr.long_term, ...(persisted.brrrr.long_term ?? {}) },
    },
    rental: {
      ...initial.rental,
      ...persisted.rental,
      long_term: { ...initial.rental.long_term, ...(persisted.rental.long_term ?? {}) },
    },
    rehabLineItems: lineItems,
    rehabCustomItems: customItems,
    cashflowOverrides: persisted.monthly_cash_flow ?? initial.cashflowOverrides,
  };
}

/**
 * Compute the rehab diff payload to send with the next save.
 *
 *   insert: current items missing an `id` (newly added in the UI)
 *   update: current items whose label/amount diverges from the cached row
 *   delete: cached row ids no longer present in the current arrays
 *
 * All three buckets are returned, even when empty — the hook is responsible
 * for skipping operations on empty buckets. When every bucket is empty the
 * caller can omit `rehab` from the patch entirely.
 */
export function buildRehabDiff(
  originalRows: RehabLineItemRow[],
  currentLineItems: NonNullable<RehabSettings["line_items"]>,
  currentCustomItems: NonNullable<RehabSettings["custom_items"]>,
): RehabLineItemsDiff {
  const insert: NonNullable<RehabLineItemsDiff["insert"]> = [];
  const update: NonNullable<RehabLineItemsDiff["update"]> = [];

  const seenIds = new Set<string>();
  const byId = new Map(originalRows.map((r) => [r.id, r]));

  const collect = (
    items: Array<{ id?: string; label: string; amount: number }>,
    category: "standard" | "custom",
  ) => {
    for (const item of items) {
      if (!item.id) {
        insert.push({ category, label: item.label, amount: item.amount });
        continue;
      }
      seenIds.add(item.id);
      const orig = byId.get(item.id);
      if (!orig) continue;
      const sameLabel = (orig.label ?? "") === item.label;
      const sameAmount = Number(orig.estimated_cost) === item.amount;
      if (!sameLabel || !sameAmount) {
        update.push({ id: item.id, label: item.label, amount: item.amount });
      }
    }
  };

  collect(currentLineItems, "standard");
  collect(currentCustomItems, "custom");

  const deleted = originalRows
    .filter((r) => !seenIds.has(r.id))
    .map((r) => r.id);

  return { insert, update, delete: deleted };
}

/** True when the diff has no inserts, updates, or deletes. */
export function isRehabDiffEmpty(diff: RehabLineItemsDiff): boolean {
  return (
    !diff.insert?.length &&
    !diff.update?.length &&
    !diff.delete?.length
  );
}

/**
 * Build the persistence payload from the current state.
 *
 * The four headline inputs (purchase / repair_cost / arv / rent) write to
 * top-level columns. `monthly_cash_flow` is saved as the FULL resolved
 * object (user overrides merged with computed defaults) so the row never
 * persists an empty `{}` that would clobber the defaults on reload.
 *
 * Rehab line items are NOT included here — they live in a separate table.
 * The caller builds a diff via `buildRehabDiff(originalRows, line, custom)`
 * and attaches it under `rehab` on the patch when there's anything to write.
 */
export function toPersistedPatch(state: DealInputsState, strategy: Strategy) {
  const enabled = state.financingEnabled;
  // Financing is tracked per-strategy: only stamp the active strategy's loan
  // slots so toggling financing for, say, Flip doesn't touch BRRRR/Rental.
  // (BRRRR stamps both its hard_money + long_term slots.)
  return {
    purchase_price: state.purchase,
    repair_cost: state.rehab,
    after_repair_value: state.arv,
    monthly_rent: state.rent,
    flip: strategy === "Flip" ? withEnabledFlag(state.flip, enabled) : state.flip,
    brrrr:
      strategy === "BRRRR" ? withEnabledFlag(state.brrrr, enabled) : state.brrrr,
    rental:
      strategy === "Rental"
        ? withEnabledFlag(state.rental, enabled)
        : state.rental,
    monthly_cash_flow: resolveCashflowForSave(state),
    closing_pct: state.closingPct,
    closing_auto: state.closingAuto,
    mao: state.mao,
    use_section8_rent: state.useSection8Rent,
  };
}

/**
 * Resolve the full monthly_cash_flow payload that gets saved. Anywhere the
 * user has not explicitly overridden a row we fill in the computed default
 * (same formulas used by deriveMetrics / MonthlyCashflow panel), so the
 * persisted JSONB always contains every field rather than an empty `{}`.
 */
function resolveCashflowForSave(state: DealInputsState): CashflowOverrides {
  const overrides = state.cashflowOverrides ?? {};
  const rent = state.rent;
  const purchase = state.purchase;
  return {
    gross: round2(overrides.gross ?? rent),
    other: round2(overrides.other ?? 0),
    vacancy: round2(overrides.vacancy ?? 0),
    mgmt: round2(overrides.mgmt ?? 0),
    taxes: round2(overrides.taxes ?? purchase * 0.00103),
    insurance: round2(overrides.insurance ?? purchase * 0.0005),
    maint: round2(overrides.maint ?? rent * 0.08),
    capex: round2(overrides.capex ?? rent * 0.06),
    hoa: round2(overrides.hoa ?? 0),
    utilities: round2(overrides.utilities ?? 0),
    mortgage: overrides.mortgage != null ? round2(overrides.mortgage) : overrides.mortgage,
  };
}

/* ────────────────────────────────────────────────────────────────────────── *
 * Derivations — pure calculations from the input state
 * ────────────────────────────────────────────────────────────────────────── */

export type FinancingMode = "short_term" | "long_term" | "none";

export interface DerivedMetrics {
  /** Closing cost in $ (Purchase * closingPct%) */
  closingDollar: number;
  /** Down payment in $ (Purchase * (100 - LTV)%) for the active mode */
  downPayment: number;
  /** Total cash required up-front: down + rehab + closing + origination fee */
  totalCash: number;
  /** Active loan inputs for the chosen mode */
  loan: LoanInputs;
  loanAmount: number;
  origFee: number;
  /**
   * Monthly debt service for the active loan. Depends on the mode: `long_term`
   * amortizes, `short_term` (hard money) is an interest-only carry with the
   * principal ballooned at sale/refi. Consumers multiply this by a hold period,
   * so an amortizing figure here would charge principal every month AND again
   * as the payoff at close.
   */
  monthlyDebt: number;
  /** Effective monthly rent (already reflects Section-8 swap upstream) */
  monthlyRent: number;
  annualRent: number;
  /** Operating expense rollup pulled from cashflow overrides + sensible defaults */
  cashflow: ReturnType<typeof computeMonthlyCashflow>;
  /** Total of all rehab line items + custom items */
  rehabTotal: number;
  /** MAO value (ARV × pct − rehab) */
  maoValue: number;
  /** ARV − (Purchase + Rehab + Closing) — quick equity look */
  equity: number;
  /** Price ÷ Sqft when sqft is known */
  pricePerSqft: number;
  /** Cap rate (%) */
  capRatePct: number;
  /** Cash-on-cash (%) */
  cocPct: number;
  /** GRM */
  grmValue: number;
  /** 1% rule (%) — rent ÷ price */
  onePctValue: number;
  /** DSCR */
  dscrValue: number;
}

interface DeriveArgs {
  state: DealInputsState;
  strategy: Strategy;
  /** Numeric square footage for $/sqft. Parsed from `snapshot.sqft`. */
  sqft: number;
  /** When financing is disabled the loan figures all zero out. */
  financingEnabled?: boolean;
}

/** Pick the active loan based on the strategy. Hard money for Flip/BRRRR, long-term for Rental. */
export function pickActiveLoan(state: DealInputsState, strategy: Strategy): {
  loan: LoanInputs;
  mode: FinancingMode;
} {
  if (strategy === "Rental") {
    return { loan: state.rental.long_term ?? { ltv: 0, rate: 0, term: 0, points: 0 }, mode: "long_term" };
  }
  if (strategy === "Flip") {
    return { loan: state.flip.hard_money ?? { ltv: 0, rate: 0, term: 0, points: 0 }, mode: "short_term" };
  }
  // BRRRR: short-term during acquisition (the long-term refi is computed
  // separately in BrrrrSnapshot panel).
  return { loan: state.brrrr.hard_money ?? { ltv: 0, rate: 0, term: 0, points: 0 }, mode: "short_term" };
}

export function deriveMetrics({
  state,
  strategy,
  sqft,
  financingEnabled = true,
}: DeriveArgs): DerivedMetrics {
  const { purchase, rehab, arv, rent } = state;

  // Closing
  const closingDollar = closingCostFn(purchase, state.closingPct);

  // Active loan + monthly debt service
  const { loan, mode } = pickActiveLoan(state, strategy);
  const enabled = financingEnabled && loan.ltv > 0;
  const loanAmt = enabled ? loanAmountFn(purchase, loan.ltv) : 0;
  const origFeeAmt = enabled ? originationFee(loanAmt, loan.points) : 0;
  const termMonths = enabled
    ? termInMonths(loan, mode === "long_term" ? "years" : "months")
    : 0;
  const monthlyDebt = !enabled
    ? 0
    : mode === "long_term"
      ? amortizedEmi(loanAmt, loan.rate, termMonths)
      : interestOnlyMonthly(loanAmt, loan.rate);
  const downPmt = enabled ? downPaymentFn(purchase, loan.ltv) : purchase;
  const totalCash = downPmt + rehab + closingDollar + origFeeAmt;

  // Rent / NOI / cashflow
  const overrides = state.cashflowOverrides ?? {};
  const cf = computeMonthlyCashflow({
    gross: overrides.gross ?? rent,
    other: overrides.other ?? 0,
    vacancy: overrides.vacancy ?? 0,
    mgmt: overrides.mgmt ?? 0,
    taxes: overrides.taxes ?? purchase * 0.00103,
    insurance: overrides.insurance ?? purchase * 0.0005,
    maint: overrides.maint ?? rent * 0.08,
    capex: overrides.capex ?? rent * 0.06,
    hoa: overrides.hoa ?? 0,
    utilities: overrides.utilities ?? 0,
    mortgage: overrides.mortgage ?? monthlyDebt,
  });

  // Rehab line items total
  const rehabTotal =
    (state.rehabLineItems ?? []).reduce((sum, i) => sum + (i.amount || 0), 0) +
    (state.rehabCustomItems ?? []).reduce((sum, i) => sum + (i.amount || 0), 0);

  const maoValue = maoFn(arv, rehab, state.mao.pct);
  const equity = arv - (purchase + rehab + closingDollar);
  const pricePerSqft = sqft > 0 ? purchase / sqft : 0;

  return {
    closingDollar,
    downPayment: downPmt,
    totalCash,
    loan,
    loanAmount: loanAmt,
    origFee: origFeeAmt,
    monthlyDebt,
    monthlyRent: rent,
    annualRent: rent * 12,
    cashflow: cf,
    rehabTotal,
    maoValue,
    equity,
    pricePerSqft,
    capRatePct: capRate(cf.noi * 12, purchase),
    cocPct: cashOnCash(cf.annualNet, totalCash),
    grmValue: grm(purchase, rent * 12),
    onePctValue: onePctRule(rent, purchase),
    dscrValue: dscr(cf.noi, monthlyDebt),
  };
}

/** Numeric sqft from `MockProperty.sqft` (may be string "1,727" or number). */
export function parseSqft(value: string | number | null | undefined): number {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  return Number(value.replace(/[^0-9.]/g, "")) || 0;
}
